import './CuratedLeads.css';
import { readZapierLeadsContext, saveZapierLeadsContext, clearZapierLeadsContext } from '../zapier-leads-context.js';
import { ZAPIER_SETTINGS_ROUTE } from '../zapier-settings.js';
import { SelectedLeadDelivery, ZapierSummary } from '../components/ZapierDelivery.jsx';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api.js';
import Beacon from '../components/Beacon.jsx';
import {
  ConfirmationDialog,
  useConfirmationDialog,
} from '../components/ConfirmationDialog.jsx';
import {
  IconArchive,
  IconChevronLeft,
  IconClose,
  IconCopy,
  IconDownload,
  IconFilter,
  IconInbox,
  IconInfo,
  IconLock,
  IconMail,
  IconMegaphone,
  IconMegaphoneOff,
  IconPhone,
  IconRefresh,
  IconShieldCheck,
  IconTrash,
} from '../components/icons.jsx';
import Menu from '../components/Menu.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import Select from '../components/Select.jsx';
import { Button, Chip, Pill } from '../components/primitives.jsx';
import { PALETTE } from '../palette.js';
import { hasNamedTenantAction } from '../product-policy.js';

export const LEAD_STATUSES = Object.freeze(['new', 'contacted', 'booked', 'lost']);
export const ARCHIVE_FILTERS = Object.freeze(['exclude', 'include', 'only']);

const EMPTY_FILTERS = Object.freeze({
  campaignId: '',
  adId: '',
  formId: '',
  status: '',
  from: '',
  to: '',
  archived: 'exclude',
});

// Contact answers already render as the identity block, so the Responses
// section filters these keys out of the display only; `fields` stays intact
// for the API read model and the CSV export.
export const CONTACT_FIELD_KEYS = Object.freeze([
  'full_name',
  'name',
  'first_name',
  'last_name',
  'email',
  'phone_number',
  'phone',
]);

const FILTER_KEYS = Object.freeze(Object.keys(EMPTY_FILTERS));
const FILTER_DEBOUNCE_MS = 250;
const NOTICE_AUTO_CLEAR_MS = 5000;

function activeCompanyId(session) {
  const memberships = session?.user?.memberships || [];
  return session?.user?.activeCompany?.id
    || session?.user?.tenant?.companyId
    || memberships[0]?.companyId
    || '';
}

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

function text(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function firstObject(...values) {
  return values.find((value) => value && typeof value === 'object' && !Array.isArray(value)) || {};
}

function safeStatus(value) {
  const normalized = text(value).toLowerCase();
  return LEAD_STATUSES.includes(normalized) ? normalized : 'new';
}

function safeRevision(value) {
  const revision = Number(value);
  return Number.isSafeInteger(revision) && revision >= 0 ? revision : 0;
}

function normalizeReference(value, fallbackName) {
  const reference = firstObject(value);
  const id = text(reference.id || reference.resourceId || reference.providerId);
  const name = text(reference.name || reference.label) || fallbackName;
  // OGC-452: a deleted campaign keeps its captured name and is flagged so the
  // page can label it and group it instead of linking to it.
  return id || name ? { id, name, ...(reference.deleted === true ? { deleted: true } : {}) } : null;
}

// Facets group live campaigns first; deleted ones sit under "Deleted".
export function campaignFacetGroups(campaigns) {
  const live = [];
  const deleted = [];
  for (const campaign of Array.isArray(campaigns) ? campaigns : []) {
    (campaign?.deleted ? deleted : live).push(campaign);
  }
  return { live, deleted };
}

// The Campaign filter's options: all, live campaigns, then a Deleted group.
export function campaignFilterOptions(campaigns) {
  const { live, deleted } = campaignFacetGroups(campaigns);
  const option = (campaign) => ({ value: campaign.id, label: campaign.name });
  return [
    { value: '', label: 'All campaigns' },
    ...live.map(option),
    ...(deleted.length ? [{ label: 'Deleted', options: deleted.map(option) }] : []),
  ];
}

const ARCHIVED_FILTER_OPTIONS = Object.freeze([
  { value: 'exclude', label: 'Active only' },
  { value: 'include', label: 'Include archived' },
  { value: 'only', label: 'Archived only' },
]);

// Ad attribution is optional: a form opened from a shared link or the Page
// CTA has no provider ad id. `id` doubles as the `adId` filter value.
function normalizeAdReference(value) {
  const ad = firstObject(value);
  const providerAdId = text(ad.providerAdId || ad.provider_ad_id || ad.id);
  if (!providerAdId) return null;
  return {
    id: text(ad.id) || providerAdId,
    name: text(ad.name || ad.label || ad.ad_label) || null,
    renovationType: text(ad.renovationType || ad.renovation_type_name || ad.renovation_type) || null,
    providerAdId,
    providerAdSetId: text(ad.providerAdSetId || ad.provider_ad_set_id) || null,
    creativeVersionId: text(ad.creativeVersionId || ad.creative_version_id) || null,
  };
}

function normalizeContact(value = {}) {
  const contact = firstObject(value);
  return {
    name: text(contact.name || contact.fullName || contact.full_name) || null,
    email: text(contact.email) || null,
    phone: text(contact.phone || contact.phoneNumber || contact.phone_number) || null,
  };
}

function normalizeNotes(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((note) => note && typeof note === 'object')
    .map((note) => ({
      id: text(note.id),
      body: text(note.body || note.note),
      createdAt: note.createdAt || note.created_at || null,
      author: text(note.author?.name || note.actor?.name || note.authorName || note.author_name) || 'Team member',
    }))
    .filter((note) => note.id && note.body);
}

export function normalizeCuratedLead(value = {}) {
  const capture = firstObject(value.capture, value);
  const source = firstObject(capture.source, value.source);
  const management = firstObject(value.management, capture.management);
  const erasure = firstObject(value.erasure, capture.erasure, management.erasure);
  const erasedAt = erasure.erasedAt
    || erasure.erased_at
    || capture.erasedAt
    || capture.erased_at
    || value.erasedAt
    || value.erased_at
    || null;
  const erased = erasure.erased === true || Boolean(erasedAt);
  const fields = erased ? {} : firstObject(
    capture.normalizedFields,
    capture.normalized_fields,
    capture.fields,
    capture.answers,
    value.normalizedFields,
    value.normalized_fields,
    value.fields,
    value.answers,
  );
  return {
    id: text(value.id || value.leadId || capture.id),
    contact: erased
      ? normalizeContact()
      : normalizeContact(firstObject(capture.contact, value.contact, capture)),
    source: {
      campaign: normalizeReference(
        firstObject(capture.campaign, source.campaign, value.campaign),
        'Meta campaign',
      ),
      form: normalizeReference(
        firstObject(capture.form, source.form, value.form),
        'Instant Form',
      ),
      page: normalizeReference(firstObject(capture.page, source.page, value.page), 'Facebook Page'),
      ad: normalizeAdReference(firstObject(capture.ad, source.ad, value.ad)),
    },
    fields,
    providerCreatedAt: capture.providerCreatedAt
      || capture.provider_created_at
      || value.providerCreatedAt
      || value.provider_created_at
      || value.createdAt
      || value.created_at
      || null,
    capturedAt: capture.capturedAt
      || capture.captured_at
      || value.capturedAt
      || value.captured_at
      || value.createdAt
      || value.created_at
      || null,
    erased,
    erasedAt,
    management: {
      status: safeStatus(management.status),
      archivedAt: management.archivedAt || management.archived_at || null,
      revision: safeRevision(management.revision),
    },
    notes: erased ? [] : normalizeNotes(value.notes || capture.notes),
  };
}

function normalizeFacet(value, fallbackName) {
  const item = normalizeReference(value, fallbackName);
  return item?.id ? item : null;
}

function uniqueReferences(values) {
  const byId = new Map();
  for (const item of values.filter(Boolean)) {
    const current = byId.get(item.id);
    // The facet (server-marked) wins over a row reference; a deleted flag from
    // either side is kept.
    byId.set(item.id, current ? { ...item, ...current, ...((item.deleted || current.deleted) ? { deleted: true } : {}) } : item);
  }
  return [...byId.values()].sort((left, right) => left.name.localeCompare(right.name));
}

function adFacet(value) {
  const ad = normalizeAdReference(value);
  if (!ad) return null;
  const campaignId = text(value?.campaignId || value?.campaign_id || value?.campaign?.id);
  return { id: ad.id, name: ad.name || 'Campaign ad', ...(campaignId ? { campaignId } : {}) };
}

function normalizeStatusCounts(value) {
  const counts = firstObject(value);
  if (!['all', ...LEAD_STATUSES].some((key) => counts[key] !== undefined)) return null;
  const count = (key) => {
    const number = Number(counts[key]);
    return Number.isSafeInteger(number) && number >= 0 ? number : 0;
  };
  return {
    all: count('all'),
    new: count('new'),
    contacted: count('contacted'),
    booked: count('booked'),
    lost: count('lost'),
  };
}

function mergeStatusCounts(current, next) {
  return next || current || null;
}

export function normalizeCuratedLeadListResponse(response = {}) {
  const items = (Array.isArray(response.items) ? response.items : [])
    .map(normalizeCuratedLead)
    .filter((lead) => lead.id);
  const facets = firstObject(response.facets, response.filters);
  const page = firstObject(response.page, response.pagination);
  const nextBefore = text(
    page.nextBefore
    || page.nextCursor
    || response.nextBefore
    || response.nextCursor,
  ) || null;
  return {
    items,
    page: {
      limit: Number(page.limit || response.limit) || 25,
      hasMore: page.hasMore === true || response.hasMore === true || Boolean(nextBefore),
      nextBefore,
    },
    facets: {
      campaigns: uniqueReferences([
        ...(Array.isArray(facets.campaigns) ? facets.campaigns.map((value) => normalizeFacet(value, 'Meta campaign')) : []),
        ...items.map((lead) => lead.source.campaign),
      ]),
      forms: uniqueReferences([
        ...(Array.isArray(facets.forms) ? facets.forms.map((value) => normalizeFacet(value, 'Instant Form')) : []),
        ...items.map((lead) => lead.source.form),
      ]),
      ads: uniqueReferences([
        ...(Array.isArray(facets.ads) ? facets.ads.map(adFacet) : []),
        ...items.map((lead) => adFacet(lead.source.ad)),
      ]),
    },
    counts: normalizeStatusCounts(response.counts || facets.counts || facets.statusCounts),
  };
}

export function buildCuratedLeadListPath(filters = {}, before = '') {
  const params = new URLSearchParams({ limit: '25' });
  const normalized = { ...EMPTY_FILTERS, ...filters };
  if (before) params.set('before', text(before));
  if (normalized.campaignId) params.set('campaignId', text(normalized.campaignId));
  if (normalized.adId) params.set('adId', text(normalized.adId));
  if (normalized.formId) params.set('formId', text(normalized.formId));
  if (LEAD_STATUSES.includes(normalized.status)) params.set('status', normalized.status);
  if (normalized.from) params.set('from', text(normalized.from));
  if (normalized.to) params.set('to', text(normalized.to));
  params.set('archived', ARCHIVE_FILTERS.includes(normalized.archived) ? normalized.archived : 'exclude');
  return `/api/saas/leads?${params.toString()}`;
}

export function buildCuratedLeadExportPath(filters = {}) {
  const params = new URLSearchParams();
  const normalized = { ...EMPTY_FILTERS, ...filters };
  if (normalized.campaignId) params.set('campaignId', text(normalized.campaignId));
  if (normalized.adId) params.set('adId', text(normalized.adId));
  if (normalized.formId) params.set('formId', text(normalized.formId));
  if (LEAD_STATUSES.includes(normalized.status)) params.set('status', normalized.status);
  if (normalized.from) params.set('from', text(normalized.from));
  if (normalized.to) params.set('to', text(normalized.to));
  params.set('archived', ARCHIVE_FILTERS.includes(normalized.archived) ? normalized.archived : 'exclude');
  return `/api/saas/leads/export.csv?${params.toString()}`;
}

export function validateLeadFilters(filters = {}) {
  if (filters.from && filters.to && filters.from > filters.to) {
    return 'The start date must be on or before the end date.';
  }
  return '';
}

export function activeLeadFilterCount(filters = {}) {
  const normalized = { ...EMPTY_FILTERS, ...filters };
  return [
    normalized.adId,
    normalized.formId,
    normalized.from,
    normalized.to,
    normalized.archived !== 'exclude' ? normalized.archived : '',
  ].filter(Boolean).length;
}

export function hasActiveLeadFilters(filters = {}) {
  const normalized = { ...EMPTY_FILTERS, ...filters };
  return Boolean(normalized.status || normalized.campaignId || activeLeadFilterCount(normalized) > 0);
}

function pluralLeads(count) {
  return `${count} lead${count === 1 ? '' : 's'}`;
}

// Header summary. Counts honour every non-status filter of the same request,
// so "6 active leads · 2 new" always describes the current scope.
export function leadInboxSummary({ counts = null, filters = EMPTY_FILTERS, itemCount = 0, hasMore = false } = {}) {
  const archived = ARCHIVE_FILTERS.includes(filters?.archived) ? filters.archived : 'exclude';
  if (counts) {
    if (counts.all === 0 && !hasActiveLeadFilters(filters)) return 'No leads yet';
    if (archived === 'only') return `${counts.all} archived lead${counts.all === 1 ? '' : 's'}`;
    if (archived === 'include') return pluralLeads(counts.all);
    return `${counts.all} active lead${counts.all === 1 ? '' : 's'} · ${counts.new} new`;
  }
  if (itemCount === 0 && !hasActiveLeadFilters(filters)) return 'No leads yet';
  return `${itemCount}${hasMore ? '+' : ''} lead${itemCount === 1 && !hasMore ? '' : 's'} shown`;
}

export function leadListLabel({ itemCount = 0, filters = EMPTY_FILTERS } = {}) {
  return `${pluralLeads(itemCount)}${filters?.archived === 'only' ? ' · archived' : ''}`;
}

export function leadStatusPresentation(value) {
  return {
    new: { label: 'New', tone: 'accent' },
    contacted: { label: 'Contacted', tone: 'warn' },
    booked: { label: 'Booked', tone: 'live' },
    lost: { label: 'Lost', tone: 'neutral' },
  }[safeStatus(value)];
}

export function canManageCuratedLeads(session) {
  return hasNamedTenantAction(session?.user?.productPolicy, 'leads.manage');
}

export function curatedLeadPrivacyCapabilities(session) {
  const productPolicy = session?.user?.productPolicy;
  return {
    canErase: hasNamedTenantAction(productPolicy, 'leads.erase'),
    canExport: hasNamedTenantAction(productPolicy, 'leads.export'),
    canVerifyErasure: hasNamedTenantAction(productPolicy, 'leads.verify_erasure'),
  };
}

export function curatedLeadExportContext({ session, companyId, filters = EMPTY_FILTERS } = {}) {
  const user = firstObject(session?.user);
  const role = firstObject(user.role);
  const policy = firstObject(user.productPolicy);
  const actorId = text(user.id || user.userId || user.email);
  const roleId = text(role.id || role.key || role.slug || role.name || user.role);
  const actions = Array.isArray(policy.actions)
    ? policy.actions.filter((action) => typeof action === 'string').sort()
    : [];
  return JSON.stringify({
    actorId,
    authType: text(user.auth?.type),
    canExport: curatedLeadPrivacyCapabilities(session).canExport,
    companyId: text(companyId),
    exportPath: buildCuratedLeadExportPath(filters),
    policyCapability: text(policy.capability),
    policyActions: actions,
    roleId,
  });
}

export function curatedLeadPrivacyContext({ session, companyId, leadId } = {}) {
  const user = firstObject(session?.user);
  const role = firstObject(user.role);
  const policy = firstObject(user.productPolicy);
  const capabilities = curatedLeadPrivacyCapabilities(session);
  const actions = Array.isArray(policy.actions)
    ? policy.actions.filter((action) => typeof action === 'string').sort()
    : [];
  return JSON.stringify({
    actorId: text(user.id || user.userId || user.email),
    authType: text(user.auth?.type),
    canErase: capabilities.canErase,
    canVerifyErasure: capabilities.canVerifyErasure,
    companyId: text(companyId),
    leadId: text(leadId),
    policyCapability: text(policy.capability),
    policyActions: actions,
    roleId: text(role.id || role.key || role.slug || role.name || user.role),
  });
}

export function isCuratedLeadPrivacyContextCurrent({
  requestCompany,
  currentCompany,
  requestContext,
  currentContext,
} = {}) {
  return Boolean(
    text(requestCompany)
    && requestCompany === currentCompany
    && requestContext
    && requestContext === currentContext
  );
}

export function canMaterializeCuratedLeadExport({
  requestCompany,
  currentCompany,
  requestContext,
  currentContext,
  requestSequence,
  currentSequence,
  privacyPending = false,
} = {}) {
  return Boolean(
    text(requestCompany)
    && requestCompany === currentCompany
    && requestContext
    && requestContext === currentContext
    && requestSequence === currentSequence
    && privacyPending !== true
  );
}

export function exportStateAfterContextChange(
  current,
  { companyId, previousContext, nextContext } = {},
) {
  if (previousContext === nextContext) return current;
  return { companyId: text(companyId), busy: false, message: '' };
}

function curatedLeadPrivacyScope(companyId, leadId) {
  return `${text(companyId)}:${text(leadId)}`;
}

export function redactCuratedLeadForPendingErasure(value = {}) {
  const lead = firstObject(value);
  return {
    ...lead,
    contact: normalizeContact(),
    fields: {},
    notes: [],
    privacyPending: true,
  };
}

export function redactCuratedLeadForConfirmedErasure(value = {}, erasedAt = null) {
  const lead = firstObject(value);
  return {
    ...lead,
    contact: normalizeContact(),
    fields: {},
    notes: [],
    erased: true,
    erasedAt: erasedAt || lead.erasedAt || null,
    privacyPending: false,
  };
}

export function redactCuratedLeadListForPendingErasure(value, leadId) {
  if (!value || !Array.isArray(value.items)) return value;
  const targetLeadId = text(leadId);
  return {
    ...value,
    items: value.items.map((lead) => (
      lead?.id === targetLeadId ? redactCuratedLeadForPendingErasure(lead) : lead
    )),
  };
}

export function replaceCuratedLeadInList(value, lead) {
  if (!value || !Array.isArray(value.items) || !lead?.id) return value;
  return {
    ...value,
    items: value.items.map((current) => current?.id === lead.id ? { ...lead, notes: [] } : current),
  };
}

export function removeCuratedLeadFromList(value, leadId) {
  if (!value || !Array.isArray(value.items) || !leadId) return value;
  if (!value.items.some((current) => current?.id === leadId)) return value;
  return { ...value, items: value.items.filter((current) => current?.id !== leadId) };
}

// Whether a mutated lead still belongs to the applied list scope (status
// filter + archived scope); the other filters never change on a mutation.
export function leadMatchesFilters(lead, filters = EMPTY_FILTERS) {
  const normalized = { ...EMPTY_FILTERS, ...filters };
  if (LEAD_STATUSES.includes(normalized.status) && lead?.management?.status !== normalized.status) return false;
  const archived = Boolean(lead?.management?.archivedAt);
  if (normalized.archived === 'only') return archived;
  if (normalized.archived === 'include') return true;
  return !archived;
}

// Silent page-1 refetch after a mutation: fresh rows replace their
// counterparts, rows from later loaded pages are kept (deduped by id) and the
// current cursor stays so 'Load more' continues where it was.
export function mergeCuratedLeadListPages(current, response) {
  if (!current?.paged || !Array.isArray(current.items)) return response;
  const fresh = new Set(response.items.map((lead) => lead.id));
  return {
    ...response,
    items: [...response.items, ...current.items.filter((lead) => !fresh.has(lead.id))],
    page: current.page,
    paged: true,
  };
}

// Desktop auto-selection (no lead in the route): the first row, except while
// a list read is in flight (keep what was shown) or while a note draft pins
// the remembered lead (keep it as long as it is still listed).
export function autoSelectedLead({ listBusy = false, companyId = '', remembered = {}, items = [], draftPinned = false } = {}) {
  const rememberedId = remembered?.companyId === companyId ? (remembered.leadId || '') : '';
  if (listBusy) return rememberedId;
  const rows = Array.isArray(items) ? items : [];
  if (draftPinned && rememberedId && rows.some((lead) => lead?.id === rememberedId)) return rememberedId;
  return rows[0]?.id || '';
}

export function createCuratedLeadPrivacyGuard() {
  const entries = new Map();

  function exactEntry(scope, operationId) {
    const entry = entries.get(scope);
    return entry?.operationId === operationId ? entry : null;
  }

  function applyLead({ companyId, lead } = {}) {
    if (!lead?.id) return lead;
    const scope = curatedLeadPrivacyScope(companyId, lead.id);
    const entry = entries.get(scope);
    if (lead.erased === true || lead.erasedAt) {
      entries.set(scope, Object.freeze({
        ...entry,
        companyId: text(companyId),
        leadId: text(lead.id),
        operationId: entry?.operationId ?? null,
        status: 'erased',
        erasedAt: lead.erasedAt || null,
        checkable: false,
      }));
      return redactCuratedLeadForConfirmedErasure(lead, lead.erasedAt);
    }
    if (!entry) return lead;
    if (entry.status === 'erased') {
      return redactCuratedLeadForConfirmedErasure(lead, entry.erasedAt);
    }
    return redactCuratedLeadForPendingErasure(lead);
  }

  return Object.freeze({
    begin({ companyId, leadId, operationId } = {}) {
      const scope = curatedLeadPrivacyScope(companyId, leadId);
      if (entries.has(scope)) return null;
      entries.set(scope, Object.freeze({
        status: 'pending',
        companyId: text(companyId),
        leadId: text(leadId),
        operationId,
        erasedAt: null,
        checkable: false,
      }));
      return scope;
    },

    owns({ scope, operationId } = {}) {
      return Boolean(exactEntry(scope, operationId));
    },

    markErased({ scope, operationId, erasedAt = null } = {}) {
      const entry = exactEntry(scope, operationId);
      if (!entry) return false;
      entries.set(scope, Object.freeze({
        ...entry,
        status: 'erased',
        erasedAt: erasedAt || null,
      }));
      return true;
    },

    markCheckable({ scope, operationId } = {}) {
      const entry = exactEntry(scope, operationId);
      if (!entry || entry.status !== 'pending') return false;
      entries.set(scope, Object.freeze({ ...entry, checkable: true }));
      return true;
    },

    beginCheck({ scope, operationId } = {}) {
      const entry = exactEntry(scope, operationId);
      if (!entry || entry.status !== 'pending' || entry.checkable !== true) return false;
      entries.set(scope, Object.freeze({ ...entry, checkable: false }));
      return true;
    },

    hasPending({ companyId } = {}) {
      const targetCompanyId = text(companyId);
      return [...entries.values()].some((entry) => (
        entry.status === 'pending' && entry.companyId === targetCompanyId
      ));
    },

    pendingOperation({ companyId, leadId } = {}) {
      const scope = curatedLeadPrivacyScope(companyId, leadId);
      const entry = entries.get(scope);
      if (!entry || entry.status !== 'pending') return null;
      return {
        scope,
        operationId: entry.operationId,
        checkable: entry.checkable === true,
      };
    },

    clear({ scope, operationId } = {}) {
      const entry = exactEntry(scope, operationId);
      if (!entry || entry.status !== 'pending') return false;
      entries.delete(scope);
      return true;
    },

    applyLead,

    applyList({ companyId, value } = {}) {
      if (!value || !Array.isArray(value.items)) return value;
      return {
        ...value,
        items: value.items.map((lead) => applyLead({ companyId, lead })),
      };
    },
  });
}

export function leadErasureConfirmationOptions() {
  return Object.freeze({
    eyebrow: 'Lead privacy',
    title: 'Erase this lead’s personal data?',
    description: "This permanently removes personal data stored by BeyondLeads and cannot be undone. BeyondLeads erasure does not remove Meta's copy.",
    confirmLabel: 'Erase personal data',
    cancelLabel: 'Keep lead data',
    tone: 'danger',
  });
}

function leadExportFilename(now = new Date()) {
  const date = now instanceof Date ? now : new Date(now);
  const datePart = Number.isFinite(date.getTime()) ? date.toISOString().slice(0, 10) : 'export';
  return `beyondleads-leads-${datePart}.csv`;
}

function downloadCsvText(csv, filename) {
  if (
    typeof document === 'undefined'
    || typeof Blob === 'undefined'
    || typeof URL === 'undefined'
    || typeof URL.createObjectURL !== 'function'
  ) throw new Error('CSV downloads are unavailable');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.hidden = true;
  document.body?.append(anchor);
  anchor.click();
  anchor.remove();
  globalThis.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export async function downloadCuratedLeadsCsv({
  companyId,
  filters = EMPTY_FILTERS,
  apiClient = api,
  downloadImpl = downloadCsvText,
  shouldDownload = () => true,
  now = new Date(),
} = {}) {
  const csv = await apiClient.get(
    buildCuratedLeadExportPath(filters),
    tenantHeaders(companyId),
  );
  if (typeof csv !== 'string') throw new Error('Lead export response was invalid');
  const filename = leadExportFilename(now);
  if (!shouldDownload()) return { filename, downloaded: false };
  downloadImpl(csv, filename);
  return { filename, downloaded: true };
}

export async function eraseCuratedLeadPersonalData({ companyId, leadId, apiClient = api } = {}) {
  const response = await apiClient.post(
    `/api/saas/leads/${encodeURIComponent(text(leadId))}/erasure`,
    { confirmed: true },
    tenantHeaders(companyId),
  );
  return firstObject(response?.erasure);
}

export async function verifyCuratedLeadErasure({ companyId, leadId, apiClient = api } = {}) {
  const response = await apiClient.get(
    `/api/saas/leads/${encodeURIComponent(text(leadId))}/erasure`,
    tenantHeaders(companyId),
  );
  return firstObject(response?.verification);
}

export function isDefinitiveCuratedLeadErasureFailure(error) {
  const status = Number(error?.status || 0);
  return error instanceof ApiError
    && status >= 400
    && status < 500
    && ![408, 425, 429].includes(status);
}

export function isCuratedLeadErasureVerificationComplete(verification = {}) {
  return verification.erased === true
    && verification.residualPii === false
    && verification.eventHashValid === true;
}

export async function reconcileCuratedLeadErasure({
  companyId,
  leadId,
  allowNotErased = false,
  apiClient = api,
} = {}) {
  const expectedLeadId = text(leadId);
  try {
    const verification = await verifyCuratedLeadErasure({
      companyId,
      leadId: expectedLeadId,
      apiClient,
    });
    if (text(verification.leadId) !== expectedLeadId) return { outcome: 'uncertain' };
    if (isCuratedLeadErasureVerificationComplete(verification)) {
      return { outcome: 'erased', erasedAt: null };
    }
    if (
      !allowNotErased
      || verification.erased !== false
      || verification.residualPii !== true
      || verification.eventHashValid !== false
    ) return { outcome: 'uncertain' };

    const response = await apiClient.get(
      `/api/saas/leads/${encodeURIComponent(expectedLeadId)}`,
      tenantHeaders(companyId),
    );
    const lead = normalizeCuratedLead(response?.lead || response);
    if (!lead.id || lead.id !== expectedLeadId) return { outcome: 'uncertain' };
    if (lead.erased === true || lead.erasedAt) {
      return { outcome: 'erased', erasedAt: lead.erasedAt || null };
    }
    return { outcome: 'not_erased', lead };
  } catch {
    return { outcome: 'uncertain' };
  }
}

export async function retryCuratedLeadErasure({ companyId, leadId, apiClient = api } = {}) {
  try {
    const erasure = await eraseCuratedLeadPersonalData({ companyId, leadId, apiClient });
    if (erasure.erased === true) {
      return { outcome: 'erased', erasedAt: erasure.erasedAt || null };
    }
  } catch {
    // A lost response is not proof that either the original write or this
    // idempotent retry failed. Fall through to positive-only verification.
  }
  return reconcileCuratedLeadErasure({
    companyId,
    leadId,
    allowNotErased: false,
    apiClient,
  });
}

export function scopedLeadData(value, ownerCompanyId, currentCompanyId) {
  return ownerCompanyId && ownerCompanyId === currentCompanyId ? value : null;
}

export function scopedLeadDetail(value, ownerScope, currentCompanyId, currentLeadId) {
  return ownerScope === `${currentCompanyId}:${currentLeadId}` ? value : null;
}

export function scopedMutationBusy(value, ownerCompanyId, currentCompanyId) {
  return Boolean(value && ownerCompanyId && ownerCompanyId === currentCompanyId);
}

function contactName(lead) {
  if (lead?.privacyPending) return 'Personal data hidden';
  if (lead?.erased || lead?.erasedAt) return 'Personal data erased';
  return lead?.contact?.name || lead?.contact?.email || lead?.contact?.phone || 'Unnamed lead';
}

// List-row second line: the ad that converted, the campaign for organic form
// opens, then the form as a last resort.
export function leadRowSubtitle(lead) {
  return lead?.source?.ad?.name
    || lead?.source?.campaign?.name
    || lead?.source?.form?.name
    || 'Meta Instant Form';
}

export function customResponseEntries(fields) {
  return Object.entries(firstObject(fields))
    .filter(([key]) => !CONTACT_FIELD_KEYS.includes(String(key).trim().toLowerCase()))
    .map(([key, value]) => ({ key, label: readableLabel(key), value: displayValue(value) }));
}

// 404/403 mean the lead left this company's scope (no retry); anything else
// is transient and keeps the word "unavailable" for the existing e2e contract.
export function detailFailure(error, companyName = '') {
  const status = Number(error?.status || 0);
  if (error instanceof ApiError && (status === 404 || status === 403)) {
    return {
      kind: 'gone',
      text: `This lead is no longer available in ${text(companyName) || 'the active company'}.`,
    };
  }
  return { kind: 'failed', text: 'This lead is unavailable right now.' };
}

export function leadPrivacyMenuItems({
  canManage = false,
  canErase = false,
  canVerifyErasure = false,
  erased = false,
  pending = false,
  archived = false,
  sourceDetailsOpen = false,
} = {}) {
  const privacyHidden = erased || pending;
  const items = [];
  if (canManage && !privacyHidden) {
    items.push({ id: archived ? 'restore' : 'archive', label: archived ? 'Restore lead' : 'Archive lead' });
  }
  items.push({ id: 'source', label: sourceDetailsOpen ? 'Hide source details' : 'Show source details' });
  if (canErase && !privacyHidden) {
    items.push({ id: 'erase', label: 'Erase personal data…', tone: 'danger', separatorBefore: true });
  }
  if (canVerifyErasure && erased) {
    items.push({ id: 'verify', label: 'Verify erasure', separatorBefore: true });
  }
  return items;
}

function parseDate(value) {
  if (value === null || value === undefined || value === '') return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

function formatDate(value, compact = false) {
  const date = parseDate(value);
  if (!date) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: compact ? 'medium' : 'long',
    timeStyle: 'short',
  }).format(date);
}

// "Sep 17, 9:12 AM" for the identity line; the row keeps it shorter still.
function formatSubmitted(value) {
  const date = parseDate(value);
  if (!date) return '';
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatRowTime(value) {
  const date = parseDate(value);
  if (!date) return '';
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date);
  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

function sameFilters(left = {}, right = {}) {
  return FILTER_KEYS.every((key) => (left[key] ?? '') === (right[key] ?? ''));
}

function displayValue(value) {
  if (Array.isArray(value)) return value.map(displayValue).join(', ');
  if (value && typeof value === 'object') return Object.values(value).map(displayValue).filter(Boolean).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value ?? '');
}

function readableLabel(value) {
  return String(value || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();
}

function mutationError(error) {
  if (error instanceof ApiError && error.status === 409) {
    return 'This lead was updated elsewhere — showing the latest version';
  }
  return 'The lead could not be updated safely. Try again.';
}

function applyManagementResponse(lead, response) {
  const management = firstObject(response?.management);
  if (!lead?.id || !Object.keys(management).length) return null;
  return {
    ...lead,
    management: {
      status: safeStatus(management.status ?? lead.management.status),
      archivedAt: management.archivedAt ?? management.archived_at ?? null,
      revision: safeRevision(management.revision),
    },
  };
}

function applyNoteResponse(lead, response) {
  const notes = normalizeNotes([response?.note]);
  if (!lead?.id || !notes.length) return null;
  return { ...lead, notes: [...(lead.notes || []), ...notes] };
}

function newNoteKey() {
  const random = globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `lead-note-${random}`;
}

export default function CuratedLeads({ navigate, route, session, initialFilters, zapierSummary, onManageZapier, onOpenActivity, onOpenDelivery }) {
  const companyId = activeCompanyId(session);
  const companyName = text(session?.user?.activeCompany?.name);
  const canManage = canManageCuratedLeads(session);
  const { canErase, canExport, canVerifyErasure } = curatedLeadPrivacyCapabilities(session);
  const selectedId = route?.rest?.[0] || '';
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const confirmationDialog = useConfirmationDialog();
  const companyRef = useRef(companyId);
  companyRef.current = companyId;
  const previousCompanyRef = useRef(companyId);
  const listSequence = useRef(0);
  const detailSequence = useRef(0);
  const mutationSequence = useRef(0);
  const privacySequence = useRef(0);
  const exportSequence = useRef(0);
  const exportGuardRef = useRef('');
  const exportContextStateRef = useRef('');
  const privacyOperationGuardRef = useRef('');
  const privacySelectionGuardRef = useRef(null);
  const privacyScopeGuardRef = useRef(null);
  if (!privacyScopeGuardRef.current) {
    privacyScopeGuardRef.current = createCuratedLeadPrivacyGuard();
  }
  const selectedLeadRef = useRef('');
  const autoSelectionRef = useRef({ companyId: '', leadId: '' });
  const returnFocusLeadRef = useRef('');
  const listRegionRef = useRef(null);
  const backButtonRef = useRef(null);
  const detailRegionRef = useRef(null);
  const settledDetailLayoutRef = useRef({ companyId: '', height: 0, viewportWidth: 0 });
  const noteKey = useRef(null);
  const [returnFilters] = useState(() => initialFilters || readZapierLeadsContext({ companyId, userId: session?.user?.id, role: session?.user?.role })?.filters || EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(returnFilters);
  const [filters, setFilters] = useState(returnFilters);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const [dateError, setDateError] = useState('');
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [data, setData] = useState(null);
  const [dataCompanyId, setDataCompanyId] = useState('');
  const [listPending, setListPending] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailScope, setDetailScope] = useState('');
  const [listError, setListError] = useState('');
  const [loadMoreError, setLoadMoreError] = useState('');
  const [detailError, setDetailError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [deliveryRefresh, setDeliveryRefresh] = useState(0);
  const [mutationBusy, setMutationBusy] = useState(false);
  const [mutationCompanyId, setMutationCompanyId] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [detailNotice, setDetailNotice] = useState(null);
  const [pageNotice, setPageNotice] = useState(null);
  const [noteBody, setNoteBody] = useState('');
  const [privacyState, setPrivacyState] = useState({ scope: '', busy: false, message: '' });
  const [exportState, setExportState] = useState({ companyId: '', busy: false, message: '' });
  const currentData = scopedLeadData(data, dataCompanyId, companyId);
  const listBusy = Boolean(listPending && listPending.companyId === companyId);
  // While a non-append list request is in flight the old rows stay mounted, so
  // the derived desktop auto-selection keeps whatever was shown instead of
  // jumping to the previous page's first row and back.
  // OGC-450: an unsent note draft pins the desktop auto-selection to the
  // lead it was written for, so a Refresh that brings a newer lead to the top
  // does not switch the detail pane and discard the draft. The pin lifts when
  // the draft is sent or cleared, or the lead leaves the list.
  const autoSelectedLeadId = autoSelectedLead({
    listBusy,
    companyId,
    remembered: autoSelectionRef.current,
    items: currentData?.items,
    draftPinned: noteBody.trim().length > 0,
  });
  const selectedLeadId = selectedId || autoSelectedLeadId;
  if (!listBusy) autoSelectionRef.current = { companyId, leadId: selectedLeadId };
  selectedLeadRef.current = selectedLeadId;

  async function fetchList(nextFilters = filtersRef.current, { append = false, before = '', silent = false, merge = false } = {}) {
    const requestCompany = companyId;
    const requestSelectedLead = selectedLeadRef.current;
    const selectedPendingBeforeRead = privacyScopeGuardRef.current.pendingOperation({
      companyId: requestCompany,
      leadId: requestSelectedLead,
    });
    // A page-1 read owns the sequence: it invalidates every older read,
    // including an append still in flight. An append never bumps it, so it can
    // never cancel a newer page-1 read (a chip pressed while Load more runs,
    // the silent counts refetch after a mutation, or Refresh).
    const sequence = append ? listSequence.current : ++listSequence.current;
    if (!append && !silent) setData(null);
    if (!append) {
      setListPending({ companyId: requestCompany, sequence });
      setListError('');
    }
    setLoadMoreError('');
    try {
      const response = privacyScopeGuardRef.current.applyList({
        companyId: requestCompany,
        value: normalizeCuratedLeadListResponse(await api.get(
          buildCuratedLeadListPath(nextFilters, before),
          tenantHeaders(requestCompany),
        )),
      });
      if (companyRef.current !== requestCompany || sequence !== listSequence.current) return false;
      setDataCompanyId(requestCompany);
      setData((current) => append ? {
        items: [...new Map([...(current?.items || []), ...response.items].map((lead) => [lead.id, lead])).values()],
        page: response.page,
        facets: {
          campaigns: uniqueReferences([...(current?.facets?.campaigns || []), ...response.facets.campaigns]),
          forms: uniqueReferences([...(current?.facets?.forms || []), ...response.facets.forms]),
          ads: uniqueReferences([...(current?.facets?.ads || []), ...response.facets.ads]),
        },
        counts: mergeStatusCounts(current?.counts, response.counts),
        paged: true,
      } : merge ? mergeCuratedLeadListPages(current, response) : response);
      const selectedResponseLead = selectedPendingBeforeRead
        && selectedLeadRef.current === requestSelectedLead
        ? response.items.find((lead) => lead.id === requestSelectedLead) || null
        : null;
      if (selectedResponseLead?.erased === true || selectedResponseLead?.erasedAt) {
        setDetail((current) => privacyScopeGuardRef.current.applyLead({
          companyId: requestCompany,
          lead: current,
        }));
        setPrivacyState({
          scope: selectedPendingBeforeRead.scope,
          context: privacyOperationGuardRef.current,
          busy: false,
          message: 'Personal data erasure was confirmed by BeyondLeads.',
        });
      }
      return true;
    } catch {
      if (companyRef.current === requestCompany && sequence === listSequence.current) {
        if (append) setLoadMoreError('More leads could not be loaded safely.');
        else setListError('Leads could not be loaded safely.');
      }
      return false;
    } finally {
      if (!append) setListPending((current) => (current?.sequence === sequence ? null : current));
    }
  }

  useEffect(() => {
    // The first load of a company shows the list loader; every later filter
    // change refetches silently behind the mounted rows.
    fetchList(filters, { silent: dataCompanyId === companyId && Boolean(data) });
    return () => {
      listSequence.current += 1;
    };
    // The active filter object changes only when a filter is applied.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, filters]);

  async function fetchDetail(leadId = selectedLeadRef.current, { silent = false } = {}) {
    if (!leadId) {
      setDetail(null);
      setDetailError(null);
      return false;
    }
    const requestCompany = companyId;
    const requestLead = leadId;
    const pendingBeforeRead = privacyScopeGuardRef.current.pendingOperation({
      companyId: requestCompany,
      leadId: requestLead,
    });
    const sequence = ++detailSequence.current;
    if (!silent) setDetail(null);
    setDetailError(null);
    try {
      const response = await api.get(
        `/api/saas/leads/${encodeURIComponent(requestLead)}`,
        tenantHeaders(requestCompany),
      );
      if (
        companyRef.current !== requestCompany
        || selectedLeadRef.current !== requestLead
        || sequence !== detailSequence.current
      ) return false;
      const lead = privacyScopeGuardRef.current.applyLead({
        companyId: requestCompany,
        lead: normalizeCuratedLead(response?.lead || response),
      });
      if (!lead.id || lead.id !== requestLead) throw new Error('Lead response mismatch');
      setDetailScope(`${requestCompany}:${requestLead}`);
      setDetail(lead);
      setData((current) => replaceCuratedLeadInList(current, lead));
      if (pendingBeforeRead && (lead.erased === true || lead.erasedAt)) {
        setData((current) => privacyScopeGuardRef.current.applyList({
          companyId: requestCompany,
          value: current,
        }));
        setPrivacyState({
          scope: pendingBeforeRead.scope,
          context: privacyOperationGuardRef.current,
          busy: false,
          message: 'Personal data erasure was confirmed by BeyondLeads.',
        });
      }
      return true;
    } catch (error) {
      if (
        companyRef.current === requestCompany
        && selectedLeadRef.current === requestLead
        && sequence === detailSequence.current
      ) {
        const failure = detailFailure(error, companyName);
        if (silent && failure.kind !== 'gone') {
          showDetailNotice(requestLead, 'This lead could not be refreshed. Try again.', 'error');
        } else {
          setDetailError({ ...failure, leadId: requestLead, companyId: requestCompany });
        }
      }
      return false;
    }
  }

  useEffect(() => {
    setDetailNotice(null);
    setPrivacyState({ scope: '', busy: false, message: '' });
    setNoteBody('');
    noteKey.current = null;
    fetchDetail(selectedLeadId);
    return () => { detailSequence.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, selectedLeadId]);

  useEffect(() => {
    mutationSequence.current += 1;
    privacySequence.current += 1;
    setMutationBusy(false);
    setPendingAction(null);
    setPageNotice(null);
    setDateError('');
    setLoadMoreError('');
    // A tenant switch drops the pinned lead before the new company's list is
    // read; the desktop auto-selection then picks that company's first lead.
    if (previousCompanyRef.current && previousCompanyRef.current !== companyId && selectedIdRef.current) {
      navigate('leads', { replace: true });
    }
    if (previousCompanyRef.current !== companyId) { clearZapierLeadsContext(); setDraftFilters(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); }
    previousCompanyRef.current = companyId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Instant filters: chips apply immediately; selects and dates settle after a
  // short debounce, and an invalid range keeps the previous results visible.
  useEffect(() => {
    if (sameFilters(draftFilters, filters)) {
      setDateError('');
      return undefined;
    }
    const error = validateLeadFilters(draftFilters);
    setDateError(error);
    if (error) return undefined;
    const timer = setTimeout(() => {
      setFilters({ ...draftFilters });
      if (selectedIdRef.current) navigate('leads');
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftFilters, filters, companyId]);

  const selectedLead = scopedLeadDetail(detail, detailScope, companyId, selectedLeadId);
  function rememberSettledDetailHeight() {
    const height = detailRegionRef.current?.getBoundingClientRect().height || 0;
    if (height > 0) {
      settledDetailLayoutRef.current = {
        companyId,
        height: Math.ceil(height),
        viewportWidth: Math.round(globalThis.innerWidth || 0),
      };
    }
  }

  useEffect(() => {
    const detailRegion = detailRegionRef.current;
    if (!selectedLead || !detailRegion) return undefined;
    rememberSettledDetailHeight();
    if (typeof globalThis.ResizeObserver !== 'function') return undefined;
    const observer = new globalThis.ResizeObserver(rememberSettledDetailHeight);
    observer.observe(detailRegion);
    return () => observer.disconnect();
  }, [selectedLead]);
  const selectedLeadUnsettled = Boolean(selectedLeadId && !selectedLead);
  const settledDetailLayout = settledDetailLayoutRef.current;
  const currentViewportWidth = Math.round(globalThis.innerWidth || 0);
  const matchingSettledLayout = settledDetailLayout.companyId === companyId
    && settledDetailLayout.height > 0
    && settledDetailLayout.viewportWidth === currentViewportWidth;
  const currentDetailStyle = selectedLeadUnsettled && matchingSettledLayout
    ? { minHeight: settledDetailLayout.height }
    : undefined;

  // Single-pane focus management: the list is display:none once a lead is
  // open, so focus moves to Back; on Back it returns to the row. Layout is
  // read from the CSS result, never derived from matchMedia.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const listHidden = listRegionRef.current
      ? listRegionRef.current.getClientRects().length === 0
      : false;
    if (selectedId) {
      if (listHidden && backButtonRef.current) {
        try {
          backButtonRef.current.focus({ preventScroll: true });
        } catch {
          backButtonRef.current.focus();
        }
      }
      return;
    }
    const returnLeadId = returnFocusLeadRef.current;
    returnFocusLeadRef.current = '';
    if (!returnLeadId || listHidden) return;
    const escaped = globalThis.CSS?.escape ? globalThis.CSS.escape(returnLeadId) : returnLeadId.replace(/"/g, '');
    const row = listRegionRef.current?.querySelector?.(`[data-lead-id="${escaped}"]`);
    // OGC-450: when the returned-to row is gone (erased, filtered out, paged
    // away) focus lands on the list region rather than dropping to <body>.
    const target = row || listRegionRef.current;
    if (target) {
      try {
        target.focus({ preventScroll: true });
      } catch {
        target.focus();
      }
    }
  }, [selectedId]);

  const currentMutationBusy = scopedMutationBusy(mutationBusy, mutationCompanyId, companyId);
  const currentPendingAction = currentMutationBusy ? pendingAction : null;
  const privacyScope = `${companyId}:${selectedLeadId}`;
  const privacyOperationContext = curatedLeadPrivacyContext({
    session,
    companyId,
    leadId: selectedLeadId,
  });
  privacyOperationGuardRef.current = privacyOperationContext;
  const currentPrivacyState = (
    privacyState.scope === privacyScope
    && privacyState.context === privacyOperationContext
  )
    ? privacyState
    : { scope: privacyScope, context: privacyOperationContext, busy: false, message: '' };
  const currentExportState = exportState.companyId === companyId
    ? exportState
    : { companyId, busy: false, message: '' };
  const currentDetailNotice = detailNotice
    && detailNotice.companyId === companyId
    && detailNotice.leadId === selectedLeadId
    ? detailNotice
    : null;
  const currentPageNotice = pageNotice && pageNotice.companyId === companyId ? pageNotice : null;
  const currentDetailError = detailError
    && detailError.companyId === companyId
    && detailError.leadId === selectedLeadId
    ? detailError
    : null;
  const selectedPendingOperation = privacyScopeGuardRef.current.pendingOperation({
    companyId,
    leadId: selectedLeadId,
  });
  const currentCompanyHasPendingErasure = privacyScopeGuardRef.current.hasPending({ companyId });
  const facets = currentData?.facets || { campaigns: [], forms: [], ads: [] };
  const counts = currentData?.counts || null;
  privacySelectionGuardRef.current = {
    canErase,
    canVerifyErasure,
    companyId,
    erased: selectedLead?.erased === true,
    erasedAt: selectedLead?.erasedAt || null,
    leadId: selectedLead?.id || '',
    privacyPending: selectedLead?.privacyPending === true,
  };
  const exportContext = curatedLeadExportContext({ session, companyId, filters });
  exportGuardRef.current = exportContext;

  useEffect(() => {
    const previousContext = exportContextStateRef.current;
    exportContextStateRef.current = exportContext;
    if (!previousContext) return;
    if (previousContext === exportContext) return;
    exportSequence.current += 1;
    setExportState((current) => exportStateAfterContextChange(current, {
      companyId,
      previousContext,
      nextContext: exportContext,
    }));
  }, [companyId, exportContext]);

  // Success notices clear themselves; errors stay until dismissed.
  useEffect(() => {
    if (!detailNotice || detailNotice.tone !== 'success') return undefined;
    const timer = setTimeout(() => setDetailNotice((current) => (current === detailNotice ? null : current)), NOTICE_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [detailNotice]);
  useEffect(() => {
    if (!pageNotice || pageNotice.tone !== 'success') return undefined;
    const timer = setTimeout(() => setPageNotice((current) => (current === pageNotice ? null : current)), NOTICE_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [pageNotice]);

  function showDetailNotice(leadId, message, tone = 'success') {
    setDetailNotice({ companyId: companyRef.current, leadId, text: message, tone, key: Date.now() });
  }

  function showPageNotice(message, tone = 'success') {
    setPageNotice({ companyId: companyRef.current, text: message, tone, key: Date.now() });
  }

  function updateDraftFilter(key, value) {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  }

  function applyStatusFilter(status) {
    // The whole draft applies with the chip when it is valid, so a select or
    // date change still debouncing does not fetch a second time.
    const draft = { ...draftFilters, status };
    const draftValid = !validateLeadFilters(draft);
    setDraftFilters(draft);
    setFilters((current) => {
      const next = draftValid ? draft : { ...current, status };
      return sameFilters(current, next) ? current : next;
    });
    if (selectedId) navigate('leads');
  }

  function clearFilters() {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setDateError('');
    if (selectedId) navigate('leads');
  }

  async function loadMore() {
    const before = currentData?.page?.nextBefore;
    // While a page-1 read is in flight the cursor belongs to the previous
    // result; appending with it would mix two filter sets under one chip.
    if (!before || loadingMore || listBusy) return;
    setLoadingMore(true);
    await fetchList(filtersRef.current, { append: true, before });
    setLoadingMore(false);
  }

  async function refresh() {
    setDeliveryRefresh(value => value + 1);
    zapierSummary?.refresh();
    if (refreshBusy) return;
    const requestCompany = companyId;
    const requestLead = selectedLeadRef.current;
    setRefreshBusy(true);
    try {
      const [listOk, detailOk] = await Promise.all([
        fetchList(filtersRef.current, { silent: Boolean(currentData) }),
        requestLead ? fetchDetail(requestLead, { silent: true }) : Promise.resolve(true),
      ]);
      if (companyRef.current !== requestCompany) return;
      if (listOk && detailOk) showPageNotice('Leads refreshed.');
    } finally {
      setRefreshBusy(false);
    }
  }

  async function exportCsv() {
    if (!canExport || currentExportState.busy || currentCompanyHasPendingErasure) return;
    const requestCompany = companyId;
    const requestContext = exportGuardRef.current;
    const sequence = ++exportSequence.current;
    setExportState({ companyId: requestCompany, busy: true, message: '' });
    try {
      const result = await downloadCuratedLeadsCsv({
        companyId: requestCompany,
        filters,
        shouldDownload: () => canMaterializeCuratedLeadExport({
          requestCompany,
          currentCompany: companyRef.current,
          requestContext,
          currentContext: exportGuardRef.current,
          requestSequence: sequence,
          currentSequence: exportSequence.current,
          privacyPending: privacyScopeGuardRef.current.hasPending({ companyId: requestCompany }),
        }),
      });
      if (!canMaterializeCuratedLeadExport({
        requestCompany,
        currentCompany: companyRef.current,
        requestContext,
        currentContext: exportGuardRef.current,
        requestSequence: sequence,
        currentSequence: exportSequence.current,
        privacyPending: privacyScopeGuardRef.current.hasPending({ companyId: requestCompany }),
      })) return;
      if (!result.downloaded) return;
      setExportState({
        companyId: requestCompany,
        busy: false,
        message: `Lead CSV downloaded (${result.filename}).`,
        tone: 'success',
      });
    } catch {
      if (!canMaterializeCuratedLeadExport({
        requestCompany,
        currentCompany: companyRef.current,
        requestContext,
        currentContext: exportGuardRef.current,
        requestSequence: sequence,
        currentSequence: exportSequence.current,
        privacyPending: privacyScopeGuardRef.current.hasPending({ companyId: requestCompany }),
      })) return;
      setExportState({
        companyId: requestCompany,
        busy: false,
        message: 'The lead CSV could not be downloaded safely. Try again.',
        tone: 'error',
      });
    }
  }

  useEffect(() => {
    if (exportState.tone !== 'success' || !exportState.message) return undefined;
    const timer = setTimeout(() => setExportState((current) => (
      current === exportState ? { ...current, message: '', tone: undefined } : current
    )), NOTICE_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [exportState]);

  // One mutation at a time. The response patches the detail and the list row
  // in place, then the list refetches silently so counts and ordering catch
  // up without unmounting anything.
  async function mutate({ path, method = 'post', body, message, action, applyResponse, refetchList = true }) {
    if (!selectedLead || !canManage || currentMutationBusy) return false;
    const requestCompany = companyId;
    const requestLead = selectedLead.id;
    const requestSnapshot = selectedLead;
    const sequence = ++mutationSequence.current;
    setMutationCompanyId(requestCompany);
    setMutationBusy(true);
    setPendingAction(action);
    try {
      const response = method === 'patch'
        ? await api.patch(path, body, tenantHeaders(requestCompany))
        : await api.post(path, body, tenantHeaders(requestCompany));
      if (companyRef.current !== requestCompany || sequence !== mutationSequence.current) return false;
      const selectionUnchanged = selectedLeadRef.current === requestLead;
      const updated = applyResponse ? applyResponse(requestSnapshot, response) : null;
      if (updated?.id === requestLead) {
        // The row is patched in place; a lead that left the applied scope
        // loses its row but stays open in the detail (decision 8).
        setData((current) => (leadMatchesFilters(updated, filtersRef.current)
          ? replaceCuratedLeadInList(current, updated)
          : removeCuratedLeadFromList(current, updated.id)));
        if (selectionUnchanged) {
          // An older silent detail read (Refresh) must not overwrite this
          // newer state when it lands later.
          detailSequence.current += 1;
          setDetailScope(`${requestCompany}:${requestLead}`);
          setDetail((current) => (current?.id === requestLead ? updated : current));
        }
      } else if (selectionUnchanged) {
        await fetchDetail(requestLead, { silent: true });
      }
      if (selectionUnchanged) showDetailNotice(requestLead, message, 'success');
      if (refetchList) fetchList(filtersRef.current, { silent: true, merge: true });
      return true;
    } catch (error) {
      if (
        companyRef.current === requestCompany
        && selectedLeadRef.current === requestLead
        && sequence === mutationSequence.current
      ) {
        const conflict = error instanceof ApiError && error.status === 409;
        showDetailNotice(requestLead, mutationError(error), conflict ? 'info' : 'error');
        if (conflict) await fetchDetail(requestLead, { silent: true });
      }
      return false;
    } finally {
      if (companyRef.current === requestCompany && sequence === mutationSequence.current) {
        setMutationBusy(false);
        setPendingAction(null);
      }
    }
  }

  function pinSelection(lead) {
    // A lead that leaves the active filter after a change stays open only
    // while its id is in the route (decision 8).
    if (!selectedId && lead?.id) navigate(`leads/${lead.id}`, { replace: true });
  }

  async function setStatus(status) {
    if (!selectedLead || !LEAD_STATUSES.includes(status) || status === selectedLead.management.status) return;
    if (currentMutationBusy) return;
    pinSelection(selectedLead);
    await mutate({
      path: `/api/saas/leads/${encodeURIComponent(selectedLead.id)}/status`,
      method: 'patch',
      body: { status, expectedRevision: selectedLead.management.revision },
      message: `Status saved: ${leadStatusPresentation(status).label}.`,
      action: { kind: 'status', status },
      applyResponse: applyManagementResponse,
    });
  }

  async function addNote(event) {
    event?.preventDefault?.();
    const body = noteBody.trim();
    if (!body || !selectedLead || currentMutationBusy) return;
    if (!noteKey.current) noteKey.current = newNoteKey();
    pinSelection(selectedLead);
    // Notes never change list membership or counts, so no list refetch.
    const succeeded = await mutate({
      path: `/api/saas/leads/${encodeURIComponent(selectedLead.id)}/notes`,
      body: { body, idempotencyKey: noteKey.current },
      message: 'Note added.',
      action: { kind: 'note' },
      applyResponse: applyNoteResponse,
      refetchList: false,
    });
    if (succeeded) {
      setNoteBody('');
      noteKey.current = null;
    }
  }

  async function toggleArchive() {
    if (!selectedLead) return;
    const archived = Boolean(selectedLead.management.archivedAt);
    pinSelection(selectedLead);
    await mutate({
      path: `/api/saas/leads/${encodeURIComponent(selectedLead.id)}/${archived ? 'restore' : 'archive'}`,
      body: { expectedRevision: selectedLead.management.revision },
      message: archived ? 'Lead restored.' : 'Lead archived. It stays available under More filters → Archived.',
      action: { kind: 'archive' },
      applyResponse: applyManagementResponse,
    });
  }

  async function copyContact(kind, value) {
    if (!selectedLead || !value) return;
    const label = kind === 'phone' ? 'Phone number' : 'Email';
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(value);
      showDetailNotice(selectedLead.id, `${label} copied.`, 'success');
    } catch {
      showDetailNotice(selectedLead.id, `${label} could not be copied. Select the text to copy it.`, 'error');
    }
  }

  function privacyRequestIsCurrent(requestCompany, requestContext) {
    return isCuratedLeadPrivacyContextCurrent({
      requestCompany,
      currentCompany: companyRef.current,
      requestContext,
      currentContext: privacyOperationGuardRef.current,
    });
  }

  async function refreshCurrentPrivacyScope(requestCompany, requestLead, requestContext) {
    if (!privacyRequestIsCurrent(requestCompany, requestContext)) return;
    const currentFilters = filtersRef.current;
    await Promise.all([
      fetchList(currentFilters, { silent: true }),
      selectedLeadRef.current === requestLead ? fetchDetail(requestLead, { silent: true }) : Promise.resolve(),
    ]);
  }

  async function erasePersonalData() {
    if (
      !selectedLead
      || selectedLead.erased === true
      || selectedLead.erasedAt
      || selectedLead.privacyPending
      || !canErase
      || currentPrivacyState.busy
    ) return;
    const requestCompany = companyId;
    const requestLead = selectedLead.id;
    const requestScope = `${requestCompany}:${requestLead}`;
    const confirmationContext = privacyOperationGuardRef.current;
    const approved = await confirmationDialog.request(leadErasureConfirmationOptions());
    const latest = privacySelectionGuardRef.current;
    if (
      !approved
      || confirmationContext !== privacyOperationGuardRef.current
      || latest?.companyId !== requestCompany
      || latest?.leadId !== requestLead
      || latest?.canErase !== true
      || latest?.erased === true
      || latest?.erasedAt
      || latest?.privacyPending
    ) return;

    const requestContext = confirmationContext;
    const operationId = ++privacySequence.current;
    if (!privacyScopeGuardRef.current.begin({
      companyId: requestCompany,
      leadId: requestLead,
      operationId,
    })) return;
    // A read issued before confirmation must never be allowed to restore the
    // just-cleared personal data when it resolves after this point.
    listSequence.current += 1;
    detailSequence.current += 1;
    // A CSV response created before erasure confirmation may still contain the
    // pre-erasure snapshot. Cancel its materialization before starting the write.
    exportSequence.current += 1;
    setExportState({ companyId: requestCompany, busy: false, message: '' });
    setData((current) => privacyScopeGuardRef.current.applyList({
      companyId: requestCompany,
      value: current,
    }));
    setDetail((current) => privacyScopeGuardRef.current.applyLead({
      companyId: requestCompany,
      lead: current,
    }));
    setNoteBody('');
    noteKey.current = null;
    setPrivacyState({
      scope: requestScope,
      context: requestContext,
      busy: true,
      message: '',
    });
    try {
      const erasure = await eraseCuratedLeadPersonalData({
        companyId: requestCompany,
        leadId: requestLead,
      });
      if (!privacyScopeGuardRef.current.owns({ scope: requestScope, operationId })) return;
      if (erasure.erased !== true) throw new Error('Lead erasure was not confirmed');
      privacyScopeGuardRef.current.markErased({
        scope: requestScope,
        operationId,
        erasedAt: erasure.erasedAt || null,
      });
      if (!privacyRequestIsCurrent(requestCompany, requestContext)) return;
      setData((current) => privacyScopeGuardRef.current.applyList({
        companyId: requestCompany,
        value: current,
      }));
      setDetail((current) => privacyScopeGuardRef.current.applyLead({
        companyId: requestCompany,
        lead: current,
      }));
      setPrivacyState({
        scope: requestScope,
        context: requestContext,
        busy: false,
        message: erasure.replayed
          ? 'Personal data was already erased from BeyondLeads.'
          : 'Personal data erased from BeyondLeads.',
      });
      await refreshCurrentPrivacyScope(requestCompany, requestLead, requestContext);
    } catch (error) {
      if (!privacyScopeGuardRef.current.owns({ scope: requestScope, operationId })) return;
      if (!privacyRequestIsCurrent(requestCompany, requestContext)) {
        privacyScopeGuardRef.current.markCheckable({ scope: requestScope, operationId });
        return;
      }
      const reconciliation = await reconcileCuratedLeadErasure({
        companyId: requestCompany,
        leadId: requestLead,
        allowNotErased: isDefinitiveCuratedLeadErasureFailure(error),
      });
      if (!privacyScopeGuardRef.current.owns({ scope: requestScope, operationId })) return;

      if (reconciliation.outcome === 'erased') {
        if (!privacyScopeGuardRef.current.markErased({
          scope: requestScope,
          operationId,
          erasedAt: reconciliation.erasedAt,
        })) return;
        if (!privacyRequestIsCurrent(requestCompany, requestContext)) return;
        setData((current) => privacyScopeGuardRef.current.applyList({
          companyId: requestCompany,
          value: current,
        }));
        setDetail((current) => privacyScopeGuardRef.current.applyLead({
          companyId: requestCompany,
          lead: current,
        }));
        setPrivacyState({
          scope: requestScope,
          context: requestContext,
          busy: false,
          message: 'Personal data erasure was confirmed by BeyondLeads.',
        });
        await refreshCurrentPrivacyScope(requestCompany, requestLead, requestContext);
        return;
      }

      if (reconciliation.outcome === 'not_erased') {
        const cleared = privacyScopeGuardRef.current.clear({
          scope: requestScope,
          operationId,
        });
        if (!cleared) {
          if (!privacyRequestIsCurrent(requestCompany, requestContext)) return;
          setData((current) => privacyScopeGuardRef.current.applyList({
            companyId: requestCompany,
            value: current,
          }));
          setDetail((current) => privacyScopeGuardRef.current.applyLead({
            companyId: requestCompany,
            lead: current,
          }));
          setPrivacyState({
            scope: requestScope,
            context: requestContext,
            busy: false,
            message: 'Personal data erasure was confirmed by BeyondLeads.',
          });
          return;
        }
        if (!privacyRequestIsCurrent(requestCompany, requestContext)) return;
        setData((current) => replaceCuratedLeadInList(current, reconciliation.lead));
        setDetailScope(requestScope);
        setDetail(reconciliation.lead);
        setPrivacyState({
          scope: requestScope,
          context: requestContext,
          busy: false,
          message: 'Erasure did not complete. Personal data is still stored in BeyondLeads. Try again or contact an administrator.',
        });
        return;
      }

      const checkable = privacyScopeGuardRef.current.markCheckable({
        scope: requestScope,
        operationId,
      });
      if (!privacyRequestIsCurrent(requestCompany, requestContext)) return;
      if (!checkable) {
        setData((current) => privacyScopeGuardRef.current.applyList({
          companyId: requestCompany,
          value: current,
        }));
        setDetail((current) => privacyScopeGuardRef.current.applyLead({
          companyId: requestCompany,
          lead: current,
        }));
        setPrivacyState({
          scope: requestScope,
          context: requestContext,
          busy: false,
          message: 'Personal data erasure was confirmed by BeyondLeads.',
        });
        return;
      }
      setPrivacyState({
        scope: requestScope,
        context: requestContext,
        busy: false,
        message: 'Erasure could not be confirmed. Personal data remains hidden because the stored result is uncertain.',
      });
    }
  }

  async function retryPendingErasure() {
    if (
      !selectedLead?.privacyPending
      || !canErase
      || currentPrivacyState.busy
    ) return;
    const requestCompany = companyId;
    const requestLead = selectedLead.id;
    const pending = privacyScopeGuardRef.current.pendingOperation({
      companyId: requestCompany,
      leadId: requestLead,
    });
    if (!pending?.checkable) return;
    const requestContext = privacyOperationGuardRef.current;
    if (!privacyScopeGuardRef.current.beginCheck(pending)) return;
    setPrivacyState({
      scope: pending.scope,
      context: requestContext,
      busy: true,
      message: '',
    });

    const retry = await retryCuratedLeadErasure({
      companyId: requestCompany,
      leadId: requestLead,
    });
    if (!privacyScopeGuardRef.current.owns(pending)) return;

    if (retry.outcome === 'erased') {
      if (!privacyScopeGuardRef.current.markErased({
        ...pending,
        erasedAt: retry.erasedAt,
      })) return;
      if (!privacyRequestIsCurrent(requestCompany, requestContext)) return;
      setData((current) => privacyScopeGuardRef.current.applyList({
        companyId: requestCompany,
        value: current,
      }));
      setDetail((current) => privacyScopeGuardRef.current.applyLead({
        companyId: requestCompany,
        lead: current,
      }));
      setPrivacyState({
        scope: pending.scope,
        context: requestContext,
        busy: false,
        message: 'Personal data erasure was confirmed by BeyondLeads.',
      });
      setExportState({ companyId: requestCompany, busy: false, message: '' });
      await refreshCurrentPrivacyScope(requestCompany, requestLead, requestContext);
      return;
    }

    const checkable = privacyScopeGuardRef.current.markCheckable(pending);
    if (!privacyRequestIsCurrent(requestCompany, requestContext)) return;
    if (!checkable) {
      setData((current) => privacyScopeGuardRef.current.applyList({
        companyId: requestCompany,
        value: current,
      }));
      setDetail((current) => privacyScopeGuardRef.current.applyLead({
        companyId: requestCompany,
        lead: current,
      }));
      setPrivacyState({
        scope: pending.scope,
        context: requestContext,
        busy: false,
        message: 'Personal data erasure was confirmed by BeyondLeads.',
      });
      return;
    }
    setPrivacyState({
      scope: pending.scope,
      context: requestContext,
      busy: false,
      message: 'Erasure could not be confirmed after retrying. Personal data remains hidden. Try again or contact an administrator.',
    });
  }

  async function verifyErasure() {
    if (
      !(selectedLead?.erased === true || selectedLead?.erasedAt)
      || !canVerifyErasure
      || currentPrivacyState.busy
    ) return;
    const requestCompany = companyId;
    const requestLead = selectedLead.id;
    const requestScope = `${requestCompany}:${requestLead}`;
    const requestContext = privacyOperationGuardRef.current;
    const sequence = ++privacySequence.current;
    setPrivacyState({
      scope: requestScope,
      context: requestContext,
      busy: true,
      message: '',
    });
    try {
      const verification = await verifyCuratedLeadErasure({
        companyId: requestCompany,
        leadId: requestLead,
      });
      if (
        !privacyRequestIsCurrent(requestCompany, requestContext)
        || sequence !== privacySequence.current
      ) return;
      setPrivacyState({
        scope: requestScope,
        context: requestContext,
        busy: false,
        message: isCuratedLeadErasureVerificationComplete(verification)
          ? 'Erasure verified. BeyondLeads stores no personal data for this lead.'
          : 'Erasure verification is incomplete. Try again or contact an administrator.',
      });
    } catch {
      if (
        privacyRequestIsCurrent(requestCompany, requestContext)
        && sequence === privacySequence.current
      ) {
        setPrivacyState({
          scope: requestScope,
          context: requestContext,
          busy: false,
          message: 'Erasure could not be verified safely. Try again or contact an administrator.',
        });
      }
    }
  }

  function openLead(lead) {
    rememberSettledDetailHeight();
    navigate(`leads/${lead.id}`);
  }

  function goBack() {
    returnFocusLeadRef.current = selectedId;
    navigate('leads');
  }

  const items = currentData?.items || [];
  const anyFilterActive = hasActiveLeadFilters(filters) || hasActiveLeadFilters(draftFilters);
  const moreFilterCount = activeLeadFilterCount(draftFilters);
  const firstRun = Boolean(currentData) && items.length === 0 && !hasActiveLeadFilters(filters) && !selectedId;
  const summary = leadInboxSummary({
    counts,
    filters,
    itemCount: items.length,
    hasMore: Boolean(currentData?.page?.hasMore),
  });
  const detailNoticeView = currentPrivacyState.message
    ? { text: currentPrivacyState.message, tone: 'info' }
    : currentDetailNotice;
  const pageNoticeView = currentExportState.message
    ? { text: currentExportState.message, tone: currentExportState.tone === 'error' ? 'error' : 'success' }
    : currentPageNotice;
  const exportBlockedId = 'curated-leads-export-blocked';
  const showExportBlockedHelper = canExport && currentCompanyHasPendingErasure;
  const exportBlocked = currentExportState.busy || currentCompanyHasPendingErasure;
  // The panel stays open while the range is invalid so the aria-invalid field
  // and its alert stay visible.
  const morePanelOpen = moreFiltersOpen || Boolean(dateError);
  const deliveryContext = () => ({ leadId: selectedLeadId, filters: validateLeadFilters(draftFilters) ? filters : draftFilters });

  return (
    <div className="curated-leads-page" data-detail-selected={selectedId ? 'true' : 'false'}>
      <header className="curated-leads-header">
        <div className="curated-leads-heading">
          <div className="curated-leads-eyebrow">Meta Instant Forms</div>
          <h1 className="curated-leads-title">Leads</h1>
          <div className="curated-leads-summary">
            <span>{currentData ? summary : 'Loading…'}</span>
            {!canManage && (
              <span className="curated-leads-readonly">
                <Glyph><IconLock size={12} /></Glyph>
                Read-only access
              </span>
            )}
          </div>
        </div>
        <div className="curated-leads-actions">
          {!zapierSummary && session?.user?.role === 'owner' && <Button variant="soft" size="sm" onClick={() => {
            saveZapierLeadsContext({ companyId, userId: session.user.id, role: session.user.role, ...deliveryContext() });
            navigate(ZAPIER_SETTINGS_ROUTE);
          }}>Manage Zapier</Button>}
          {canExport && (
            <Button
              variant="soft"
              size="sm"
              onClick={exportCsv}
              aria-disabled={exportBlocked ? 'true' : undefined}
              style={currentCompanyHasPendingErasure && !currentExportState.busy ? blockedButtonStyle : undefined}
              aria-label="Export CSV"
              aria-busy={currentExportState.busy ? 'true' : undefined}
              aria-describedby={showExportBlockedHelper ? exportBlockedId : undefined}
              className="curated-leads-action"
            >
              {currentExportState.busy
                ? <><Beacon size="inline" className="is-current" delayMs={0} slowAfterMs={null} /><span className="curated-leads-action-label">Exporting…</span></>
                : <><Glyph><IconDownload size={15} /></Glyph><span className="curated-leads-action-label">Export CSV</span></>}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={refresh}
            aria-label="Refresh leads"
            aria-busy={refreshBusy ? 'true' : undefined}
            aria-disabled={refreshBusy ? 'true' : undefined}
            className="curated-leads-action"
          >
            {refreshBusy
              ? <><Beacon size="inline" className="is-current" delayMs={0} slowAfterMs={null} /><span className="curated-leads-action-label">Refreshing…</span></>
              : <><Glyph><IconRefresh size={15} /></Glyph><span className="curated-leads-action-label">Refresh</span></>}
          </Button>
        </div>
      </header>
      {zapierSummary && <ZapierSummary summary={zapierSummary} owner={session.user?.role === 'owner'} onManage={() => onManageZapier(deliveryContext())} onActivity={() => onOpenActivity(deliveryContext())} />}
      {showExportBlockedHelper && (
        <p id={exportBlockedId} className="curated-leads-helper">
          Resolve the pending lead erasure before exporting personal data.
        </p>
      )}

      <NoticeRegion
        className="curated-leads-page-notice"
        notice={pageNoticeView}
        onDismiss={() => {
          setPageNotice(null);
          setExportState((current) => (current.message ? { ...current, message: '', tone: undefined } : current));
        }}
      />

      <div role="group" aria-label="Lead filters" className="curated-leads-toolbar">
        <div className="curated-leads-toolbar-row">
          <div role="group" aria-label="Status" className="curated-leads-chips">
            {[['', 'All', 'all'], ...LEAD_STATUSES.map((status) => [status, leadStatusPresentation(status).label, status])].map(([value, label, countKey]) => (
              <Chip
                key={value || 'all'}
                pressed={filters.status === value}
                tone={value || undefined}
                onClick={() => applyStatusFilter(value)}
              >
                {label}
                {counts ? <span className="bl-chip-count">{counts[countKey]}</span> : null}
              </Chip>
            ))}
          </div>
          <div className="curated-leads-toolbar-divider" aria-hidden="true" />
          <label className="curated-leads-inline-select">
            <span>Campaign</span>
            <Select
              aria-label="Campaign"
              value={draftFilters.campaignId}
              onChange={(value) => updateDraftFilter('campaignId', value)}
              options={campaignFilterOptions(facets.campaigns)}
            />
          </label>
          <button
            type="button"
            className="curated-leads-more"
            aria-expanded={morePanelOpen ? 'true' : 'false'}
            aria-controls="curated-leads-more-filters"
            aria-label={`More filters${moreFilterCount ? `, ${moreFilterCount} active` : ''}`}
            onClick={() => setMoreFiltersOpen((current) => !current)}
          >
            <Glyph><IconFilter size={15} /></Glyph>
            <span className="curated-leads-more-label">More filters</span>
            <span className="curated-leads-more-label-short">Filters</span>
            {moreFilterCount > 0 && <span className="curated-leads-more-count" aria-hidden="true">{moreFilterCount}</span>}
          </button>
          {anyFilterActive && (
            <button type="button" className="curated-leads-clear" onClick={clearFilters}>Clear</button>
          )}
        </div>
        <div id="curated-leads-more-filters" className="curated-leads-more-panel" hidden={!morePanelOpen}>
          <label>
            <span className="curated-leads-label">Ad</span>
            <Select
              className="bl-select-block"
              aria-label="Ad"
              value={draftFilters.adId}
              onChange={(value) => updateDraftFilter('adId', value)}
              options={[{ value: '', label: 'All ads' }, ...facets.ads.map((ad) => ({ value: ad.id, label: ad.name }))]}
            />
          </label>
          <label>
            <span className="curated-leads-label">Instant form</span>
            <Select
              className="bl-select-block"
              aria-label="Instant Form"
              value={draftFilters.formId}
              onChange={(value) => updateDraftFilter('formId', value)}
              options={[{ value: '', label: 'All forms' }, ...facets.forms.map((form) => ({ value: form.id, label: form.name }))]}
            />
          </label>
          <label className="is-date">
            <span className="curated-leads-label">From</span>
            <input
              aria-label="From date"
              type="date"
              value={draftFilters.from}
              onChange={(event) => updateDraftFilter('from', event.target.value)}
            />
          </label>
          <label className="is-date">
            <span className="curated-leads-label">To</span>
            <input
              aria-label="To date"
              type="date"
              value={draftFilters.to}
              aria-invalid={dateError ? 'true' : undefined}
              aria-describedby={dateError ? 'curated-leads-date-error' : undefined}
              onChange={(event) => updateDraftFilter('to', event.target.value)}
            />
          </label>
          {dateError && (
            <div id="curated-leads-date-error" role="alert" className="curated-leads-date-error">
              {dateError} Showing the previous results.
            </div>
          )}
          <label>
            <span className="curated-leads-label">Archived</span>
            <Select
              className="bl-select-block"
              aria-label="Archived"
              value={draftFilters.archived}
              onChange={(value) => updateDraftFilter('archived', value)}
              options={ARCHIVED_FILTER_OPTIONS}
            />
          </label>
          {anyFilterActive && (
            <button type="button" className="curated-leads-clear-button curated-leads-clear-all" onClick={clearFilters}>Clear all filters</button>
          )}
        </div>
      </div>

      {firstRun ? (
        <div className="curated-leads-first-run">
          <Glyph><IconInbox size={32} stroke={1.5} /></Glyph>
          <h2>Your inbox is ready</h2>
          <p>New submissions from your Meta Instant Forms appear here shortly after the form is sent. Come back here or press Refresh to check.</p>
          <a href="#/campaigns" className="curated-leads-first-run-link">View your campaigns</a>
        </div>
      ) : (
        <div
          className="curated-leads-workbench"
          data-detail-selected={selectedId ? 'true' : 'false'}
        >
          <section
            ref={listRegionRef}
            className="curated-leads-list"
            aria-label="Leads"
            tabIndex={-1}
            aria-busy={listBusy ? 'true' : undefined}
          >
            <div className="curated-leads-list-header">
              <span className="curated-leads-label" role="status" aria-live="polite">
                {currentData ? leadListLabel({ itemCount: items.length, filters }) : ''}
              </span>
            </div>
            {listError && (
              <div role="alert" className="curated-leads-inline-notice">
                <span>{listError}</span>
                <Button variant="ghost" size="sm" onClick={() => fetchList(filtersRef.current, { silent: Boolean(currentData) })}>
                  Try again
                </Button>
              </div>
            )}
            {!currentData ? (
              listError ? null : (
                <ProtectedPageLoader
                  title="Loading leads"
                  body="Checking the active company before showing Meta form contacts."
                  minHeight={420}
                  slowAfterMs={8000}
                />
              )
            ) : (
              <>
                {items.length === 0 && (
                  <div className="curated-leads-empty">
                    <Glyph><IconInbox size={28} /></Glyph>
                    <h2>No matching leads</h2>
                    <span>Try a different status or campaign.</span>
                    <Button variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>
                  </div>
                )}
                {items.length > 0 && (
                  <ul role="list" className="curated-leads-rows">
                    {items.map((lead) => {
                      const status = leadStatusPresentation(lead.management.status);
                      const active = selectedLeadId === lead.id;
                      const erased = lead.erased === true || Boolean(lead.erasedAt);
                      const unread = lead.management.status === 'new' && !erased && !lead.privacyPending;
                      return (
                        <li key={lead.id}>
                          <button
                            type="button"
                            className={`curated-leads-row${unread ? ' is-unread' : ''}`}
                            data-lead-id={lead.id}
                            data-selection={active ? (selectedId ? 'route' : 'auto') : undefined}
                            onClick={() => openLead(lead)}
                            aria-current={active ? 'true' : undefined}
                          >
                            <span aria-hidden="true" className={`curated-leads-row-dot${unread ? ' is-new' : ''}`} />
                            <span className="curated-leads-row-main">
                              <span className={`curated-leads-row-name${erased || lead.privacyPending ? ' is-erased' : ''}`}>{contactName(lead)}</span>
                              <span className="curated-leads-row-sub">{leadRowSubtitle(lead)}</span>
                            </span>
                            <span className="curated-leads-row-aside">
                              <Pill tone={status.tone} style={rowPillStyle}>{status.label}</Pill>
                              <span className="curated-leads-row-time">{formatRowTime(lead.providerCreatedAt)}</span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {currentData.page.hasMore && (
                  <div className="curated-leads-load-more">
                    {loadMoreError && <span role="alert" className="curated-leads-date-error">{loadMoreError}</span>}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={loadMore}
                      aria-disabled={loadingMore || listBusy ? 'true' : undefined}
                      aria-busy={loadingMore ? 'true' : undefined}
                    >
                      {loadingMore
                        ? <><Beacon size="inline" className="is-current" delayMs={0} slowAfterMs={null} /> Loading…</>
                        : loadMoreError ? 'Try again' : 'Load more'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </section>

          <section
            ref={detailRegionRef}
            className="curated-lead-detail"
            aria-label="Lead details"
            style={currentDetailStyle}
          >
            {selectedId && (
              <button
                ref={backButtonRef}
                type="button"
                className="curated-leads-back"
                onClick={goBack}
              >
                <Glyph><IconChevronLeft size={16} /></Glyph>
                Back to leads
              </button>
            )}
            <NoticeRegion
              notice={detailNoticeView}
              onDismiss={() => {
                setDetailNotice(null);
                if (currentPrivacyState.message) {
                  setPrivacyState({ ...currentPrivacyState, message: '' });
                }
              }}
            />
            {selectedLeadId && !selectedLead && !currentDetailError ? (
              <ProtectedPageLoader title="Loading lead" minHeight={420} />
            ) : currentDetailError ? (
              <div role="alert" className="curated-leads-inline-notice">
                <span>{currentDetailError.text}</span>
                {currentDetailError.kind !== 'gone' && (
                  <Button variant="ghost" size="sm" onClick={() => fetchDetail(selectedLeadId)}>Try again</Button>
                )}
              </div>
            ) : selectedLead ? (
              <LeadDetail
                key={selectedLead.id}
                lead={selectedLead}
                canErase={canErase}
                canManage={canManage}
                canVerifyErasure={canVerifyErasure}
                mutationBusy={currentMutationBusy}
                pendingAction={currentPendingAction}
                noteBody={noteBody}
                onNoteBodyChange={(value) => { setNoteBody(value); noteKey.current = null; }}
                onAddNote={addNote}
                onArchive={toggleArchive}
                onCopy={copyContact}
                onRetryPendingErasure={retryPendingErasure}
                onErase={erasePersonalData}
                onSetStatus={setStatus}
                onVerifyErasure={verifyErasure}
                privacyBusy={currentPrivacyState.busy}
                privacyCheckable={selectedPendingOperation?.checkable === true}
                delivery={zapierSummary ? { companyId, owner: session.user?.role === 'owner', enabled: true, refreshKey: deliveryRefresh,
                  onDetails: eventId => onOpenDelivery(eventId, deliveryContext()) } : null}
              />
            ) : null}
          </section>
        </div>
      )}
      <ConfirmationDialog {...confirmationDialog.dialogProps} />
    </div>
  );
}

// Decorative glyph wrapper: icons.jsx does not forward aria attributes, so
// every icon on this page is hidden from assistive technology here.
function Glyph({ children }) {
  return <span aria-hidden="true" className="curated-leads-glyph">{children}</span>;
}

// One persistent role="status" region per pane whose text is swapped; it has
// no height while empty so announcing a result never shifts the workbench.
function NoticeRegion({ notice, onDismiss, className = '' }) {
  const filled = Boolean(notice?.text);
  return (
    <div
      role="status"
      aria-live="polite"
      className={`curated-leads-notice ${filled ? 'is-filled' : 'is-empty'}${className ? ` ${className}` : ''}`}
      data-tone={filled ? notice.tone || 'success' : undefined}
    >
      {filled && (
        <>
          <span className="curated-leads-notice-text">{notice.text}</span>
          <button type="button" className="curated-leads-notice-dismiss" aria-label="Dismiss" onClick={onDismiss}>
            <Glyph><IconClose size={14} /></Glyph>
          </button>
        </>
      )}
    </div>
  );
}

function LeadDetail({
  lead,
  canErase = false,
  canManage = false,
  canVerifyErasure = false,
  mutationBusy = false,
  pendingAction = null,
  noteBody = '',
  onNoteBodyChange = () => {},
  onAddNote = () => {},
  onArchive = () => {},
  onCopy = () => {},
  onRetryPendingErasure = () => {},
  onErase = () => {},
  onSetStatus = () => {},
  onVerifyErasure = () => {},
  privacyBusy = false,
  privacyCheckable = false,
  delivery = null,
}) {
  const [sourceDetailsOpen, setSourceDetailsOpen] = useState(false);
  const status = leadStatusPresentation(lead.management.status);
  const responses = customResponseEntries(lead.fields);
  const archived = Boolean(lead.management.archivedAt);
  const erased = lead.erased === true || Boolean(lead.erasedAt);
  const privacyPending = lead.privacyPending === true;
  const privacyHidden = erased || privacyPending;
  const statusEditable = canManage && !privacyHidden;
  const ad = lead.source?.ad || null;
  const campaign = lead.source?.campaign || null;
  const notes = [...(lead.notes || [])].reverse();
  const submitted = formatSubmitted(lead.providerCreatedAt);
  const menuBusy = (mutationBusy && pendingAction?.kind === 'archive') || (privacyBusy && !privacyPending);
  const menuItems = leadPrivacyMenuItems({
    canManage,
    canErase,
    canVerifyErasure,
    erased,
    pending: privacyPending,
    archived,
    sourceDetailsOpen,
  }).map((item) => ({
    ...item,
    icon: item.id === 'archive' || item.id === 'restore'
      ? <IconArchive size={16} />
      : item.id === 'source'
        ? <IconInfo size={16} />
        : item.id === 'erase'
          ? <IconTrash size={16} />
          : <IconShieldCheck size={16} />,
    disabled: mutationBusy || privacyBusy,
    onSelect: item.id === 'archive' || item.id === 'restore'
      ? onArchive
      : item.id === 'source'
        ? () => setSourceDetailsOpen((current) => !current)
        : item.id === 'erase'
          ? onErase
          : onVerifyErasure,
  }));
  const noteCount = notes.length === 1 ? '1 note' : `${notes.length} notes`;

  function handleNoteKeyDown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') onAddNote(event);
  }

  return (
    <>
      <div className="curated-lead-identity">
        <div className="curated-lead-identity-text">
          <h2 className="curated-lead-name" tabIndex={-1}>{contactName(lead)}</h2>
          <div className="curated-lead-meta">
            <span>
              {privacyPending
                ? 'Erasure result not yet confirmed'
                : submitted ? `Submitted ${submitted}` : 'Submission time unknown'}
            </span>
            {campaign?.name && <span><span aria-hidden="true" className="curated-lead-meta-sep">·</span>{campaign.name}{campaign.deleted ? ' (deleted)' : ''}</span>}
            {lead.source?.form?.name && <span><span aria-hidden="true" className="curated-lead-meta-sep">·</span>{lead.source.form.name}</span>}
            {archived && <Pill tone="neutral" style={rowPillStyle}>Archived</Pill>}
            {privacyPending && <Pill tone="neutral" style={rowPillStyle}>Erasure pending</Pill>}
          </div>
          {ad ? (
            <div className="curated-lead-ad">
              <Glyph><IconMegaphone size={15} /></Glyph>
              <span>From ad —</span>
              {campaign?.id && !campaign.deleted
                ? <a href={`#/campaigns/${encodeURIComponent(campaign.id)}`}>{ad.name || 'Campaign ad'}</a>
                : <span>{ad.name || 'Campaign ad'}</span>}
              {campaign?.deleted && <span className="curated-lead-tag">Deleted campaign</span>}
              {ad.renovationType && <span className="curated-lead-tag">{ad.renovationType}</span>}
            </div>
          ) : (
            <div className="curated-lead-ad is-none">
              <Glyph><IconMegaphoneOff size={15} /></Glyph>
              <span>No ad attribution — form opened directly, not from an ad.</span>
            </div>
          )}
        </div>
        <Menu label="More actions" items={menuItems} busy={menuBusy} />
      </div>

      <div className="curated-lead-status-row">
        <span className="curated-leads-label" aria-hidden="true">Status</span>
        {statusEditable ? (
          <div role="radiogroup" aria-label="Lead status">
            {LEAD_STATUSES.map((value) => {
              const option = leadStatusPresentation(value);
              const saving = mutationBusy && pendingAction?.kind === 'status' && pendingAction.status === value;
              return (
                <Chip
                  key={value}
                  role="radio"
                  size="setter"
                  tone={value}
                  checked={lead.management.status === value}
                  busy={saving}
                  aria-disabled={mutationBusy ? 'true' : undefined}
                  onClick={() => { if (!mutationBusy) onSetStatus(value); }}
                >
                  {option.label}
                </Chip>
              );
            })}
          </div>
        ) : (
          <>
            <Pill tone={status.tone} style={rowPillStyle}>{status.label}</Pill>
            <span className="curated-lead-status-hint">
              {privacyHidden
                ? privacyPending
                  ? 'Status is frozen while the erasure result is confirmed.'
                  : 'Status is frozen after erasure.'
                : 'Read-only access · status changes require lead-management access.'}
            </span>
          </>
        )}
      </div>

      <LeadPrivacyActions
        canErase={canErase}
        canVerifyErasure={canVerifyErasure}
        erased={erased}
        erasedAt={lead.erasedAt}
        pending={privacyPending}
        checkable={privacyCheckable}
        busy={privacyBusy}
        onErase={onErase}
        onRetryPending={onRetryPendingErasure}
        onVerify={onVerifyErasure}
      />
      {delivery && <SelectedLeadDelivery {...delivery} key={lead.id} lead={{ ...lead, privacyPending: privacyPending || privacyBusy }} />}

      {!privacyHidden && (
        <>
          <div className="curated-lead-contact">
            <ContactLine icon={IconMail} kind="email" value={lead.contact.email} href={lead.contact.email ? `mailto:${lead.contact.email}` : null} onCopy={onCopy} />
            <ContactLine icon={IconPhone} kind="phone" value={lead.contact.phone} href={lead.contact.phone ? `tel:${String(lead.contact.phone).replace(/\s+/g, '')}` : null} onCopy={onCopy} />
          </div>

          <section className="curated-lead-section curated-lead-responses" aria-labelledby="curated-lead-responses-title">
            <h3 id="curated-lead-responses-title">Responses</h3>
            {responses.length === 0
              ? <p className="curated-lead-muted">This form only asked for contact details.</p>
              : (
                <dl>
                  {responses.map((entry) => (
                    <div key={entry.key} style={responseRowStyle}>
                      <dt>{entry.label}</dt>
                      <dd>{entry.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
          </section>

          <section className="curated-lead-section curated-lead-notes-section" aria-labelledby="curated-lead-notes-title">
            <div className="curated-lead-section-head">
              <h3 id="curated-lead-notes-title">Notes</h3>
              <span className="curated-lead-note-count">{noteCount}</span>
            </div>
            {canManage && (
              <form className="curated-lead-composer" onSubmit={onAddNote}>
                <label htmlFor="curated-lead-note-input" className="bl-visually-hidden">Add a note</label>
                <textarea
                  id="curated-lead-note-input"
                  aria-label="Add a note"
                  value={noteBody}
                  onChange={(event) => onNoteBodyChange(event.target.value)}
                  onKeyDown={handleNoteKeyDown}
                  maxLength={4000}
                  rows={2}
                  placeholder="Record the next follow-up step…"
                  style={controlStyle}
                  disabled={mutationBusy}
                />
                <div className="curated-lead-composer-actions">
                  <Button
                    type="submit"
                    size="sm"
                    variant={noteBody.trim() ? 'accent' : 'soft'}
                    disabled={!noteBody.trim()}
                    style={noteBody.trim() ? undefined : blockedButtonStyle}
                    aria-disabled={mutationBusy ? 'true' : undefined}
                    aria-busy={mutationBusy && pendingAction?.kind === 'note' ? 'true' : undefined}
                  >
                    {mutationBusy && pendingAction?.kind === 'note'
                      ? <><Beacon size="inline" className="is-current" delayMs={0} slowAfterMs={null} /> Adding…</>
                      : 'Add note'}
                  </Button>
                </div>
              </form>
            )}
            {notes.length === 0
              ? <p className="curated-lead-muted">No notes yet.</p>
              : (
                <ol role="list" aria-label="Lead notes" className="curated-lead-notes">
                  {notes.map((note) => (
                    <li key={note.id} className="curated-lead-note">
                      <p>{note.body}</p>
                      <span>{note.author} · {formatDate(note.createdAt, true)}</span>
                    </li>
                  ))}
                </ol>
              )}
          </section>
        </>
      )}

      {sourceDetailsOpen && (
        <section className="curated-lead-source" aria-labelledby="curated-lead-source-title">
          <div className="curated-lead-source-head">
            <h3 id="curated-lead-source-title" className="curated-leads-label">Source details</h3>
            <button type="button" className="curated-lead-source-close" aria-label="Hide source details" onClick={() => setSourceDetailsOpen(false)}>
              <Glyph><IconClose size={14} /></Glyph>
            </button>
          </div>
          <dl>
            <dt>Facebook Page</dt><dd>{lead.source?.page?.name || 'Unknown'}</dd>
            <dt>Instant Form</dt><dd>{lead.source?.form?.name || 'Unknown'}</dd>
            <dt>Campaign</dt><dd>{campaign?.name || 'Unknown'}</dd>
            <dt>Ad</dt>
            <dd>
              {ad ? <>{ad.name || 'Campaign ad'} <span className="curated-lead-mono">{ad.providerAdId}</span></> : 'No ad attribution'}
            </dd>
            <dt>Ad set</dt><dd className="curated-lead-mono">{ad?.providerAdSetId || '—'}</dd>
            <dt>Submitted on Meta</dt><dd>{formatDate(lead.providerCreatedAt)}</dd>
            <dt>Received by BeyondLeads</dt><dd>{formatDate(lead.capturedAt)}</dd>
            <dt>Lead ID</dt><dd className="curated-lead-mono">{lead.id}</dd>
          </dl>
        </section>
      )}
    </>
  );
}

// The erased banner and the erasure-pending notice. Erase and Verify live in
// the ⋯ menu (leadPrivacyMenuItems); Retry stays here because it belongs to the
// pending state. Renders nothing for a live lead.
export function LeadPrivacyActions({
  canErase = false,
  canVerifyErasure = false,
  erased = false,
  erasedAt = null,
  pending = false,
  checkable = false,
  busy = false,
  onRetryPending,
}) {
  const isErased = erased === true || Boolean(erasedAt);
  const showRetryPending = canErase && pending && checkable;
  if (!isErased && !pending) return null;

  if (pending) {
    return (
      <div className="curated-lead-banner">
        <Glyph><IconShieldCheck size={18} /></Glyph>
        <span className="curated-lead-banner-body">
          <span>
            <strong>Erasure confirmation pending.</strong>
            {' '}Personal data remains hidden because the stored erasure result is not yet confirmed.
          </span>
          {showRetryPending && (
            <Button size="sm" variant="soft" onClick={onRetryPending} disabled={busy} aria-busy={busy ? 'true' : undefined}>
              {busy ? <><Beacon size="inline" className="is-current" delayMs={0} slowAfterMs={null} /> Retrying…</> : 'Retry erasure'}
            </Button>
          )}
        </span>
      </div>
    );
  }

  return (
    <div className="curated-lead-banner">
      <Glyph><IconShieldCheck size={18} /></Glyph>
      <span>
        <strong>Personal data erased{erasedAt ? ` ${formatDate(erasedAt)}` : ''}.</strong>
        {' '}Contact details, form responses and notes were removed from BeyondLeads. Source and status history are kept. Meta retains its own copy.
        {canVerifyErasure ? ' Verify erasure from the More actions menu.' : ''}
      </span>
    </div>
  );
}

function ContactLine({ icon: Icon, kind, value, href, onCopy }) {
  const label = kind === 'phone' ? 'Copy phone' : 'Copy email';
  return (
    <div className="curated-lead-contact-line">
      <Glyph><Icon size={16} /></Glyph>
      {value
        ? <a href={href || undefined}>{value}</a>
        : <span className="curated-lead-contact-missing">{kind === 'phone' ? 'No phone number' : 'No email'}</span>}
      {value && (
        <button type="button" className="curated-lead-copy" aria-label={label} onClick={() => onCopy(kind, value)}>
          <Glyph><IconCopy size={14} /></Glyph>
        </button>
      )}
    </div>
  );
}

// The Pill primitive stays 11.5px globally; the leads page meets its 12px text
// floor locally (OGC-445 decides the global bump).
const rowPillStyle = { fontSize: 12, padding: '3px 9px' };
// Solid disabled tokens (5.2:1) for a control that is blocked rather than
// busy; the Button primitive keeps its 45% opacity elsewhere (decision 9).
const blockedButtonStyle = { opacity: 1, background: PALETTE.surfaceAlt, color: 'var(--active-ink-3)', borderColor: 'var(--active-line)', cursor: 'not-allowed' };
const responseRowStyle = { display: 'contents' };
const controlStyle = { border: `1px solid ${PALETTE.controlBorder}`, backgroundColor: PALETTE.surface, color: PALETTE.ink, font: '14px var(--active-font-ui, Inter, sans-serif)' };

export const curatedLeadsInternalsForTest = Object.freeze({
  LeadDetail,
  activeCompanyId,
  contactName,
  customResponseEntries,
  displayValue,
  leadRowSubtitle,
  newNoteKey,
  readableLabel,
  safeRevision,
  safeStatus,
});
